import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImageUpload Component
 * Allows uploading images instead of entering URLs
 * 
 * Props:
 * - value: current image URL
 * - onChange: callback when image URL changes
 * - token: auth token
 * - category: image category for organization (e.g., 'hero', 'category', 'video')
 * - label: label text
 * - className: additional classes
 */
const ImageUpload = ({ 
  value, 
  onChange, 
  token, 
  onFileUpload,
  category = 'general',
  label = 'Εικόνα',
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Μη έγκυρος τύπος αρχείου. Επιτρέπονται: JPEG, PNG, WebP, GIF');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Το αρχείο είναι πολύ μεγάλο. Μέγιστο: 10MB');
      return;
    }

    setUploading(true);

    const authToken = token || localStorage.getItem('token');

    if (!authToken) {
      toast.error('Δεν βρέθηκε έγκυρο token. Κάνε ξανά σύνδεση.');
      setUploading(false);
      return;
    }

    if (onFileUpload) {
      try {
        await onFileUpload(file);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Αποτυχία ανεβάσματος');
      } finally {
        setUploading(false);
      }
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    try {
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Use relative URL so it works on any domain
      const imageUrl = res.data.url;
      setPreview(imageUrl);
      onChange(imageUrl);
      toast.success('Η εικόνα ανέβηκε επιτυχώς!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Αποτυχία ανεβάσματος');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm text-gray-400">{label}</label>
      
      <div className="flex gap-2">
        {/* URL Input */}
        <input
          type="text"
          value={value || ''}
          onChange={handleUrlChange}
          placeholder="URL εικόνας ή ανεβάστε..."
          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary"
        />
        
        {/* Upload Button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id={`image-upload-${category}`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className="border-white/20 text-white hover:bg-white/10"
        >
          {uploading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Upload size={16} />
          )}
        </Button>
        
        {/* Remove Button */}
        {(preview || value) && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRemove}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <X size={16} />
          </Button>
        )}
      </div>

      {/* Preview */}
      {(preview || value) && (
        <div className="mt-2 relative">
          <img 
            src={preview || value} 
            alt="Preview" 
            className="w-full max-w-xs h-32 object-cover rounded-md border border-white/10"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      
      {/* Empty State */}
      {!preview && !value && (
        <div 
          className="mt-2 border-2 border-dashed border-white/10 rounded-md p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon size={24} className="mx-auto text-gray-500 mb-2" />
          <p className="text-xs text-gray-500">
            Click για upload ή paste URL παραπάνω
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
